import { toast } from "react-toastify";
import { clearAdminAuth } from "./auth_helper";

const DEFAULT_TIMEOUT_MS = 20000;

const LOGIN_PATH = "/login";

export const isLoginRoute = () => {
  const path = window.location?.pathname || "";
  return path === LOGIN_PATH || path.endsWith(LOGIN_PATH);
};

export const isLegacyNotAuthorizedMessage = (message) =>
  String(message || "").trim() === "Not Authorized";

export const redirectToLogin = () => {
  clearAdminAuth();
  if (!isLoginRoute()) {
    window.location.href = LOGIN_PATH;
  }
};

export const logoutForbidden = () => {
  toast.error("Not authorized");
  redirectToLogin();
};

/**
 * Apply admin session rules for HTTP status and legacy JSON bodies.
 * @returns {boolean} true when the caller should stop and treat the request as failed
 */
export const inspectAdminResponse = (response, data) => {
  if (response?.status === 401) {
    if (!isLoginRoute()) {
      redirectToLogin();
    }
    return true;
  }

  if (response?.status === 403) {
    logoutForbidden();
    return true;
  }

  if (response?.ok && data?.success === false && isLegacyNotAuthorizedMessage(data?.message)) {
    logoutForbidden();
    return true;
  }

  return false;
};

export const handleUnauthorized = (response) => {
  if (response?.status === 401) {
    if (!isLoginRoute()) {
      redirectToLogin();
    }
    return true;
  }
  return false;
};

const buildFetchError = (error) => {
  if (error?.name === "AbortError") {
    const timeoutError = new Error("Request timed out. Please check your network or API server.");
    timeoutError.status = 408;
    return timeoutError;
  }

  if (error?.message === "Failed to fetch" || error?.name === "TypeError") {
    const networkError = new Error("Unable to reach the server. Please check your connection or API URL.");
    networkError.status = 0;
    return networkError;
  }

  return error;
};

const buildAuthFailureError = (status, message) => {
  const error = new Error(message);
  error.status = status;
  return error;
};

export async function adminFetch(url, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  if (fetchOptions.signal) {
    fetchOptions.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    let data = null;
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      data = await response.json();
    }

    if (inspectAdminResponse(response, data)) {
      if (response.status === 403 || isLegacyNotAuthorizedMessage(data?.message)) {
        throw buildAuthFailureError(403, "Not authorized");
      }
      throw buildAuthFailureError(401, "Session expired. Please sign in again.");
    }

    if (!response.ok) {
      const error = new Error(data?.message || `Request failed (${response.status})`);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    throw buildFetchError(error);
  } finally {
    clearTimeout(timeoutId);
  }
}

export const buildQueryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  return qs ? `?${qs}` : "";
};
